/**
 * src/components/ui/DashboardNav.jsx — the dashboard's primary navigation.
 *
 * Extracted out of App.jsx's `Shell` (which still renders it, unchanged in
 * behaviour) so the nav is its own testable unit, per REDESIGN.md's Phase 3
 * "navigation for each surface" requirement. Fixes audit.md's landmark gap
 * along the way: the old inline `<nav className="sidebar">` had no
 * `aria-label`, indistinguishable from any in-page nav a screen reader
 * user's landmark list might also show — this one is labelled "Primary".
 */
import { NavLink } from 'react-router-dom';

/** Entries, in the order an artist works through their day. */
export const NAV_ITEMS = [
  { to: '/dashboard', label: 'Dashboard' },
  { to: '/inbox', label: 'Inbox' },
  { to: '/calendar', label: 'Calendar' },
  { to: '/clients', label: 'Clients' },
  { to: '/projects', label: 'Projects' },
  { to: '/flash', label: 'Flash' },
  { to: '/consent', label: 'Consent forms' },
];

/**
 * @param {{ isOwner: boolean, footer?: React.ReactNode, mobileOpen?: boolean,
 *   onNavigate?: () => void }} props
 *   `footer` — App.jsx's `Shell` still renders the artist name/mode-picker/
 *   sign-out block here, as a child of this same `<nav>`, so `.sidebar-
 *   footer`'s `margin-top: auto` (styles.css) keeps pushing it to the
 *   bottom of the shared flex container exactly as before this was
 *   extracted — moving it to a sibling element outside the nav would break
 *   that layout.
 *   `mobileOpen` — below the 760px breakpoint this same `<nav>` becomes a
 *   full-screen overlay (see styles.css); this just toggles the `.is-open`
 *   class that reveals it. Above the breakpoint it's ignored, since the
 *   sidebar is always visible there.
 *   `onNavigate` — called after a nav link is followed, so `Shell` can close
 *   the mobile overlay; irrelevant (and unused) above the breakpoint.
 *   `badges` — counts shown beside a nav entry, keyed by its `to` (the
 *   Inbox's unread count). Zero or missing shows nothing.
 * @returns {JSX.Element}
 */
export function DashboardNav({ isOwner, footer, mobileOpen, onNavigate, badges = {} }) {
  return (
    <nav
      id="mobile-nav"
      className={`sidebar${mobileOpen ? ' is-open' : ''}`}
      aria-label="Primary"
    >
      <div className="sidebar-brand">
        <img src="/favicon.svg" alt="" width="32" height="32" />
        <span>Tattoo Lab</span>
      </div>
      {NAV_ITEMS.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}
          onClick={onNavigate}
        >
          {item.label}
          {badges[item.to] ? (
            <span className="nav-badge" aria-label={`${badges[item.to]} unread`}>
              {badges[item.to] > 99 ? '99+' : badges[item.to]}
            </span>
          ) : null}
        </NavLink>
      ))}
      {isOwner ? (
        <NavLink
          to="/settings"
          className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}
          onClick={onNavigate}
        >
          Settings
        </NavLink>
      ) : null}
      {/* Every role — unlike Settings, this is the artist's own account. */}
      <NavLink
        to="/account"
        className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}
        onClick={onNavigate}
      >
        Account
      </NavLink>
      {footer}
    </nav>
  );
}
